import { useState, useEffect, useRef } from 'react';
import {
  getWorkflowNodeDropdownLabels,
  type DropdownLabelItem,
} from '@/apis/logiblocksLabelsAPI';

/** Shape of the pre-loaded label sets returned by this hook. */
export interface PreloadedLabels {
  couriers: DropdownLabelItem[];
  teams: DropdownLabelItem[];
  origins: DropdownLabelItem[];
  destinations: DropdownLabelItem[];
  alertTypes: DropdownLabelItem[];
  products: DropdownLabelItem[];
  location: DropdownLabelItem[];
}

const EMPTY_LABELS: PreloadedLabels = {
  couriers: [],
  teams: [],
  origins: [],
  destinations: [],
  alertTypes: [],
  products: [],
  location: [],
};

interface UseNodeDropdownLabelsOptions {
  isSidePannelOpen: boolean;
  workflowId: string | undefined;
  nodeId: string | undefined;
  getToken: () => string | null | undefined;
}

/**
 * Fetches the pre-loaded dropdown labels for a workflow node once when the
 * side-panel opens, so saved values that live beyond page 1 still show their
 * human-readable labels instead of raw IDs.
 *
 * Automatically resets on panel close so the next open always re-fetches.
 */
export function useNodeDropdownLabels({
  isSidePannelOpen,
  workflowId,
  nodeId,
  getToken,
}: UseNodeDropdownLabelsOptions): PreloadedLabels {
  const [preloadedLabels, setPreloadedLabels] =
    useState<PreloadedLabels>(EMPTY_LABELS);
  const labelsFetchedRef = useRef(false);

  // Fetch once when the panel first opens for this node.
  useEffect(() => {
    if (!isSidePannelOpen || !workflowId || !nodeId) return;
    if (labelsFetchedRef.current) return;
    labelsFetchedRef.current = true;

    const token = getToken();
    if (!token) return;

    getWorkflowNodeDropdownLabels(token, workflowId)
      .then((labelMap) => {
        const nodeLabels = labelMap[nodeId];
        if (!nodeLabels) return;
        setPreloadedLabels({
          couriers: nodeLabels.couriers ?? [],
          teams: nodeLabels.teams ?? [],
          origins: nodeLabels.origins ?? [],
          destinations: nodeLabels.destinations ?? [],
          alertTypes: nodeLabels.alert_types ?? [],
          products: nodeLabels.products ?? [],
          location: nodeLabels.location ?? [],
        });
      })
      .catch(() => {
        // Non-fatal: labels simply won't be pre-populated.
      });
  }, [isSidePannelOpen, workflowId, nodeId, getToken]);

  // Reset on panel close so re-opening always re-fetches.
  useEffect(() => {
    if (!isSidePannelOpen) {
      labelsFetchedRef.current = false;
    }
  }, [isSidePannelOpen]);

  return preloadedLabels;
}
